import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fingerprintData", "form"]
  static values = { 
    autoCollect: { type: Boolean, default: true },
    autoSubmit: { type: Boolean, default: false },
    submitTimeout: { type: Number, default: 3000 }
  }

  connect() {
    this.submitted = false
    
    if (this.autoCollectValue) {
      this.collectFingerprint()
    }
    
    // Set up timeout fallback if auto-submit is enabled
    if (this.autoSubmitValue) {
      setTimeout(() => {
        if (!this.submitted) {
          console.log("Fingerprint collection timeout, submitting without fingerprint data")
          this.submitted = true
          this.submitForm()
        }
      }, this.submitTimeoutValue)
    }
  }

  async collectFingerprint() {
    try {
      const fingerprint = await this.gatherFingerprintData()
      this.setFingerprintData(fingerprint)
      
      // Auto-submit if enabled
      if (this.autoSubmitValue && !this.submitted) {
        this.submitted = true
        // Small delay to ensure the hidden field is populated
        setTimeout(() => this.submitForm(), 100)
      }
    } catch (error) {
      console.warn("Fingerprint collection failed:", error)
      // Graceful degradation - continue without fingerprinting
      if (this.autoSubmitValue && !this.submitted) {
        this.submitted = true
        // Still submit even if fingerprint collection failed
        setTimeout(() => this.submitForm(), 100)
      }
    }
  }

  submitForm() {
    if (this.hasFormTarget) {
      this.formTarget.submit()
    } else if (this.element.tagName === 'FORM') {
      this.element.submit()
    } else {
      // Look for a form within the controller element
      const form = this.element.querySelector('form')
      if (form) {
        form.submit()
      }
    }
  }

  async gatherFingerprintData() {
    const fingerprint = {}

    // Screen resolution
    if (typeof screen !== "undefined") {
      fingerprint.screen_resolution = `${screen.width}x${screen.height}`
    }

    // Timezone
    try {
      fingerprint.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
      // Fallback for older browsers
      fingerprint.timezone = new Date().getTimezoneOffset().toString()
    }

    // Language
    fingerprint.language = navigator.language || navigator.userLanguage || "unknown"

    // Platform
    fingerprint.platform = navigator.platform || "unknown"

    // Browser version
    fingerprint.browser_version = this.getBrowserVersion()

    // User agent
    fingerprint.user_agent = navigator.userAgent || "unknown"

    return fingerprint
  }

  getBrowserVersion() {
    try {
      const userAgent = navigator.userAgent
      
      // Chrome
      if (userAgent.indexOf("Chrome") > -1) {
        const match = userAgent.match(/Chrome\/([0-9.]+)/)
        return match ? `Chrome/${match[1]}` : "Chrome/unknown"
      }
      
      // Firefox
      if (userAgent.indexOf("Firefox") > -1) {
        const match = userAgent.match(/Firefox\/([0-9.]+)/)
        return match ? `Firefox/${match[1]}` : "Firefox/unknown"
      }
      
      // Safari
      if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
        const match = userAgent.match(/Safari\/([0-9.]+)/)
        return match ? `Safari/${match[1]}` : "Safari/unknown"
      }
      
      // Edge
      if (userAgent.indexOf("Edge") > -1 || userAgent.indexOf("Edg") > -1) {
        const match = userAgent.match(/Edg?\/([0-9.]+)/)
        return match ? `Edge/${match[1]}` : "Edge/unknown"
      }

      // IE
      if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) {
        const match = userAgent.match(/(?:MSIE |rv:)([0-9.]+)/)
        return match ? `IE/${match[1]}` : "IE/unknown"
      }
      
      return "Unknown/unknown"
    } catch (error) {
      return "Unknown/unknown"
    }
  }

  setFingerprintData(fingerprint) {
    // If we have fingerprint data targets, set their values
    if (this.hasFingerprintDataTarget) {
      this.fingerprintDataTarget.value = JSON.stringify(fingerprint)
    }

    // Also dispatch a custom event for other controllers to listen to
    this.element.dispatchEvent(new CustomEvent("fingerprint:collected", {
      detail: { fingerprint: fingerprint },
      bubbles: true
    }))
  }

  // Manual trigger method for explicit collection
  collect() {
    this.collectFingerprint()
  }

  // Handle form submission - collect fingerprint before allowing submission
  async handleSubmit(event) {
    // If fingerprint data is already set, allow normal submission
    if (this.hasFingerprintDataTarget && this.fingerprintDataTarget.value.trim() !== "") {
      return true
    }

    // Prevent form submission
    event.preventDefault()

    try {
      // Collect fingerprint data
      const fingerprint = await this.gatherFingerprintData()
      this.setFingerprintData(fingerprint)

      // Now submit the form
      setTimeout(() => this.submitForm(), 100)
    } catch (error) {
      console.warn("Fingerprint collection failed during submit, proceeding anyway:", error)
      // Submit form even if fingerprint collection fails (graceful degradation)
      setTimeout(() => this.submitForm(), 100)
    }
  }
}